import React from 'react';
import { Play, Square } from 'lucide-react';
import { Select, InputNumber } from 'antd';
import { AnimationType } from '../types';
import { ANIMATION_DURATION, ANIMATIONS_CONFIG } from '../constants';
import { C, FONT_UI } from '../../constants/design-tokens';
import { useEditorCanvas } from '../../canvas/hooks/use-editor-canvas';
import { usePieceDispatch, usePieceSelector } from '../../model/piece-context';
import { usePlayback } from '../../playback';
import { ToolBtn } from '../../shared/ui/tool-btn';

const ANIMATION_OPTIONS = [
  { value: '', label: 'Sin animación' },
  ...ANIMATIONS_CONFIG.map(anim => ({
    value: anim.id,
    label: `${anim.icon} ${anim.label}`
  }))
];

const msToSec = (ms: number): number => Math.round((ms / 1000) * 10) / 10;
const secToMs = (sec: number): number => Math.round(sec * 1000);

const TimingLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    style={{
      fontSize: '10px',
      fontFamily: FONT_UI,
      color: C.textMuted,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      userSelect: 'none',
      flexShrink: 0
    }}
  >
    {children}
  </span>
);

type AnimationSelectorProps = {
  layout?: 'inline' | 'stacked';
};

export const AnimationSelector: React.FC<AnimationSelectorProps> = ({ layout = 'inline' }) => {
  const { canvas, selectedObject } = useEditorCanvas();
  const dispatch = usePieceDispatch();
  const { isPlaying, play, pause } = usePlayback();

  const layerId = selectedObject?.layerId;
  const layer = usePieceSelector(m => (layerId ? m.layers[layerId] : undefined));

  if (!canvas || !selectedObject || !layer || layer.kind === 'audio') return null;

  const enter = layer.enter;
  const assignedAnimation = enter?.preset ?? null;
  const delay = msToSec(layer.start);
  const duration = msToSec(enter?.duration ?? ANIMATION_DURATION);

  const setEnter = (next: { preset: AnimationType; duration: number } | null) =>
    dispatch({ type: 'setLayerEnter', id: layer.id, enter: next });

  const handleChange = (value: string) => {
    if (value) {
      setEnter({ preset: value as AnimationType, duration: enter?.duration ?? ANIMATION_DURATION });
    } else {
      setEnter(null);
    }
  };

  const handleDelayChange = (value: number | null) => {
    dispatch({ type: 'setLayerTiming', id: layer.id, start: secToMs(value ?? 0) });
  };

  const handleDurationChange = (value: number | null) => {
    if (!enter) return;
    const ms = secToMs(value ?? msToSec(ANIMATION_DURATION));
    setEnter({ ...enter, duration: ms });
  };

  const handlePreviewToggle = () => {
    if (isPlaying) pause();
    else play();
  };

  const effectSelector = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
      <span
        style={{
          fontSize: '11px',
          fontFamily: FONT_UI,
          color: C.textMuted,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          userSelect: 'none',
          flexShrink: 0
        }}
      >
        Effect
      </span>
      <Select
        value={assignedAnimation || ''}
        onChange={handleChange}
        options={ANIMATION_OPTIONS}
        size='small'
        variant='borderless'
        style={{ minWidth: '120px', fontFamily: FONT_UI }}
      />
    </div>
  );

  const timingControls = assignedAnimation ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
      <TimingLabel>Delay</TimingLabel>
      <InputNumber
        value={delay}
        onChange={handleDelayChange}
        min={0}
        max={30}
        step={0.1}
        size='small'
        controls={false}
        suffix='s'
        style={{ width: '58px', fontFamily: FONT_UI, fontSize: '11px' }}
      />
      <TimingLabel>Dur</TimingLabel>
      <InputNumber
        value={duration}
        onChange={handleDurationChange}
        min={0.1}
        max={30}
        step={0.1}
        size='small'
        controls={false}
        suffix='s'
        style={{ width: '58px', fontFamily: FONT_UI, fontSize: '11px' }}
      />
    </div>
  ) : null;

  const previewBtn = assignedAnimation ? (
    <ToolBtn onClick={handlePreviewToggle} title={isPlaying ? 'Detener' : 'Vista previa'} active={isPlaying}>
      {isPlaying ? <Square size={14} /> : <Play size={14} />}
    </ToolBtn>
  ) : null;

  if (layout === 'stacked') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>{effectSelector}</div>

        {assignedAnimation && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {timingControls}
            {previewBtn}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {effectSelector}
      {timingControls}
      {previewBtn}
    </>
  );
};
